import { css, keyframes } from 'styled-components';

import colors from '../../../styles/theme/colors';
import { fontSizes } from '../styles/scales';
import { ButtonSize, ButtonVariant } from './types';

const { gray, green, red, purple } = colors.designSystem;

export const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const buttonVariants: Record<
  ButtonVariant,
  ReturnType<typeof css>
> = {
  primary: css`
    border: 1px solid transparent;
    background-color: ${green[500]};
    color: ${gray[0]};

    &:hover:not(:disabled) {
      background-color: ${green[600]};
    }

    &:disabled {
      opacity: 0.5;
    }
  `,

  secondary: css`
    border: 1px solid transparent;
    background-color: ${gray[100]};
    color: ${gray[950]};

    &:hover:not(:disabled) {
      background-color: ${gray[200]};
    }

    &:disabled {
      background-color: ${gray[50]};
      color: ${gray[600]};
    }
  `,

  /*
    Borda e texto vêm do Figma (nó 525:2959, o "Cancelar" do modal padrão):
    stroke/neutral/disabled #bbbcbf e text/neutral/primary #131313. O MFE usava
    `border-gray-300 text-gray-700` da paleta default do Tailwind, que o porte
    mapeou para gray[400]/gray[700] — mais claro e mais apagado que o desenho.
    Alinhado ao Figma em 16/08.
  */
  outline: css`
    border: 1px solid ${gray[500]};
    background-color: ${gray[0]};
    color: ${gray[950]};

    &:hover:not(:disabled) {
      background-color: ${gray[50]};
    }

    &:disabled {
      opacity: 0.5;
    }
  `,

  'outline-primary': css`
    border: 1px solid ${green[500]};
    background-color: transparent;
    color: ${green[500]};

    &:hover:not(:disabled) {
      background-color: ${gray[50]};
    }

    &:disabled {
      opacity: 0.5;
    }
  `,

  ghost: css`
    border: 1px solid transparent;
    background-color: transparent;
    color: ${gray[700]};

    &:hover:not(:disabled) {
      background-color: ${gray[100]};
    }

    &:disabled {
      color: ${gray[600]};
    }
  `,

  destructive: css`
    border: 1px solid transparent;
    background-color: ${red[500]};
    color: ${gray[0]};

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.5;
    }
  `,

  link: css`
    border: 1px solid transparent;
    background-color: transparent;
    color: ${green[500]};

    &:hover:not(:disabled) {
      color: ${green[600]};
      text-decoration: underline;
    }

    &:disabled {
      opacity: 0.5;
    }
  `,

  'ai-outline': css`
    border: 1px solid ${purple[500]};
    background-color: transparent;
    color: ${purple[500]};

    &:hover:not(:disabled) {
      background-color: ${purple[100]};
    }

    &:disabled {
      opacity: 0.5;
    }
  `,

  'ai-primary': css`
    border: 1px solid transparent;
    background-color: ${purple[500]};
    color: ${gray[0]};

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.5;
    }
  `,

  icon: css`
    border: 1px solid ${gray[400]};
    background-color: transparent;
    color: ${gray[600]};

    &:hover:not(:disabled) {
      background-color: ${gray[100]};
      color: ${gray[950]};
    }

    &:disabled {
      opacity: 0.5;
    }
  `,
};

export const buttonSizes: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    padding: 6px 12px;
    font-size: ${fontSizes[14]};
  `,
  md: css`
    padding: 8px 16px;
    font-size: ${fontSizes[14]};
  `,
  lg: css`
    padding: 10px 20px;
    font-size: ${fontSizes[16]};
  `,
};

/** A variante `link` não tem caixa: só o tamanho do texto muda. */
export const buttonTextSizes: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    font-size: ${fontSizes[14]};
  `,
  md: css`
    font-size: ${fontSizes[14]};
  `,
  lg: css`
    font-size: ${fontSizes[16]};
  `,
};
